/** Pure text substitution; returned buyer data is always assigned to textContent. */
function personalizePolicyText(text, values) {
  let value = text.replace(/\[(ALICI_AD|ALICI_SOYAD|ALICI_AD_SOYAD|ALICI_EMAIL|SIPARIS_TOPLAMI|KARGO_TUTARI|SOZLESME_TARIHI)\]/g, (_, key) => values[key] ?? '');
  if (/^(AD-SOYAD|Ad\/Soyad\/Unvan|Teslim edilecek kişi):\s*$/.test(value.trim())) value = `${value.trim()} ${values.ALICI_AD_SOYAD}`;
  if (/^(E-posta|E-posta\/kullanıcı adı):\s*$/.test(value.trim())) value = `${value.trim()} ${values.ALICI_EMAIL || 'Henüz girilmedi'}`;
  if (/^(ADRES|Adres|Teslimat adresi|Telefon):\s*$/.test(value.trim())) value = `${value.trim()} Ödeme sayfasında girilecektir.`;
  if (/^Kargo Tutarı:\s*0,00\s*TL\s*$/.test(value.trim())) value = `Kargo Tutarı: ${values.KARGO_TUTARI}`;
  if (/^Toplam:\s*0,00\s*TL\s*$/.test(value.trim())) value = `Sepet toplamı (kargo hariç): ${values.SIPARIS_TOPLAMI}`;
  return value;
}

/** Prefill the existing cart's checkout; never rebuild lines with a permalink. */
function buildCartCheckoutUrl(base, buyer) {
  const params = new URLSearchParams({
    'checkout[email]': buyer.email,
    'checkout[shipping_address][first_name]': buyer.firstName,
    'checkout[shipping_address][last_name]': buyer.lastName,
    'checkout[billing_address][first_name]': buyer.firstName,
    'checkout[billing_address][last_name]': buyer.lastName,
  });
  return `${base}${base.includes('?') ? '&' : '?'}${params}`;
}

/** Reject a stale cart response before leaving the approved order snapshot. */
function matchesPolicyCart(cart, snapshot) {
  return cart.total_price === snapshot.total && cart.items_subtotal_price === snapshot.items_subtotal
    && cart.items?.length === snapshot.items.length
    && snapshot.items.every((item) => cart.items.some((line) => line.key === item.key
      && line.quantity === item.quantity && line.final_price === item.unit_price
      && line.final_line_price === item.line_total));
}

/**
 * One cart-page gate owns the inline policies and the summary's buyer controls.
 * This is a storefront safeguard, not server-side checkout validation.
 */
class CartConsent extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  #revision = 0;
  #version = null;
  #buyerSignature = null;
  #autofillTimer = null;
  #guest = false;
  #submitting = false;
  #rendered = new WeakMap();
  #observer = new MutationObserver(() => this.#sync());
  #cartObserver = new MutationObserver(() => this.#sync());

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('input', this.#handleBuyerInput);
    this.addEventListener('focusin', this.#handleBuyerInput);
    this.addEventListener('focusout', this.#handleBuyerInput);
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('pageshow', this.#handleResume);
    document.addEventListener('visibilitychange', this.#handleResume);
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'data-cart-empty', 'data-consent-version', 'data-policy-ready'],
    });
    const cart = this.closest('cart-items-component');
    if (cart) this.#cartObserver.observe(cart, { attributes: true, attributeFilter: ['class'] });
    this.#sync();
    // Autofill can change .value without input/change or an attribute mutation.
    // Compare only the three values; rebuild the documents only when they change.
    if (this.#autofillTimer === null) this.#autofillTimer = setInterval(this.#refreshAutofill, 250);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('input', this.#handleBuyerInput);
    this.removeEventListener('focusin', this.#handleBuyerInput);
    this.removeEventListener('focusout', this.#handleBuyerInput);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('pageshow', this.#handleResume);
    document.removeEventListener('visibilitychange', this.#handleResume);
    clearInterval(this.#autofillTimer);
    this.#autofillTimer = null;
    this.#form?.removeEventListener('submit', this.#handleSubmit, true);
    this.#form = null;
    this.#observer.disconnect();
    this.#cartObserver.disconnect();
  }

  get #checkboxes() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-consent-required]')]);
  }

  get #accepted() {
    const checkboxes = this.#checkboxes;
    const buyers = this.#buyers;
    return this.dataset.cartEmpty !== 'true' && !this.closest('cart-items-component')?.classList.contains('cart-items-disabled')
      && this.querySelector('[data-policy-ready]')?.dataset.policyReady === 'true'
      && this.#identityReady && buyers.length === 3 && buyers.every((input) => input.value.trim() && input.validity.valid)
      && checkboxes.length === 2 && checkboxes.every((input) => input.checked);
  }

  get #buyers() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-consent-buyer]')]);
  }

  get #identityReady() {
    return this.querySelector('[data-consent-identity]')?.dataset.loggedIn === 'true' || this.#guest;
  }

  get #buyerValues() {
    const value = (key) => this.#buyers.find((input) => input.dataset.consentBuyer === key)?.value.trim() ?? '';
    return { firstName: value('first-name'), lastName: value('last-name'), email: value('email') };
  }

  #invalidate() {
    this.#revision++;
    this.#checkboxes.forEach((input) => { input.checked = false; });
    const time = this.querySelector('[data-consent-time]');
    if (time instanceof HTMLInputElement) time.value = '';
    this.#showError('');
  }

  #syncBuyer() {
    const signature = JSON.stringify(this.#buyerValues);
    const changed = this.#buyerSignature !== null && this.#buyerSignature !== signature;
    this.#buyerSignature = signature;
    if (changed) this.#invalidate();
    return changed;
  }

  #refreshAutofill = () => {
    if (document.visibilityState !== 'hidden' && this.#syncBuyer()) this.#sync();
  };

  #handleResume = () => {
    if (document.visibilityState !== 'hidden') this.#sync();
  };

  #sync() {
    this.#syncBuyer();
    const identity = this.querySelector('[data-consent-identity]');
    const fields = this.querySelector('[data-consent-buyer-fields]');
    if (fields && identity?.dataset.loggedIn !== 'true') {
      if (fields.hidden !== !this.#guest) fields.hidden = !this.#guest;
      if (fields.disabled !== !this.#guest) fields.disabled = !this.#guest;
    }
    const guest = this.querySelector('[data-consent-guest]');
    if (guest?.getAttribute('aria-expanded') !== String(this.#guest)) guest?.setAttribute('aria-expanded', String(this.#guest));
    const version = this.querySelector('[data-consent-version]')?.dataset.consentVersion ?? null;
    if (this.#version !== null && this.#version !== version) this.#invalidate();
    this.#version = version;
    const accepted = this.#accepted;
    const button = this.querySelector('.cart__checkout-button');
    if (button instanceof HTMLButtonElement && button.disabled !== (!accepted || this.#submitting)) {
      button.disabled = !accepted || this.#submitting;
    }
    if (button?.getAttribute('aria-busy') !== String(this.#submitting)) button?.setAttribute('aria-busy', String(this.#submitting));
    this.dataset.consentsAccepted = String(accepted);
    this.#updateStatus();
    this.#renderPolicies();

    const form = this.#checkboxes[0]?.form ?? null;
    if (form !== this.#form) {
      this.#form?.removeEventListener('submit', this.#handleSubmit, true);
      this.#form = form;
      form?.addEventListener('submit', this.#handleSubmit, true);
    }
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-consent-buyer]')) {
      this.#handleBuyerInput(event);
      return;
    }
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-consent-required]')) return;
    // Reconcile silent autofill before recording this new acknowledgement.
    // Clear any older approvals, but keep the checkbox the customer just chose.
    const checked = event.target.checked;
    this.#syncBuyer();
    event.target.checked = checked;
    this.#revision++;
    this.#sync();
  };

  /** @param {Event} event */
  #handleBuyerInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-consent-buyer]')) return;
    this.#sync();
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    // Leave unrelated cart form actions alone.
    if (event.submitter && event.submitter.getAttribute('name') !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#submitting) return;
    this.#sync();
    if (!this.#accepted) {
      if (!this.#identityReady) {
        this.querySelector('[data-consent-guest]')?.focus();
        this.#showError('Giriş yapın veya üye olmadan devam etmeyi seçin.');
        return;
      }
      const buyer = this.#buyers.find((input) => !input.value.trim() || !input.validity.valid);
      (buyer ?? this.#checkboxes.find((input) => !input.checked))?.reportValidity();
      this.#sync();
      return;
    }
    this.#buyers.forEach((input) => { input.value = input.value.trim(); });
    void this.#checkout();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-consent-guest]') && !this.#guest) {
      this.#guest = true;
      this.#invalidate();
      this.#sync();
      this.#buyers[0]?.focus();
    }
    if (event.target.closest('[data-policy-scroll="pre"]')) {
      const pre = this.querySelector('[data-policy-panel="pre"]');
      if (pre) pre.open = true;
    }
  };

  #renderPolicies() {
    for (const panel of this.querySelectorAll('[data-policy-panel]')) this.#renderPolicy(panel);
  }

  /** @param {HTMLElement} panel */
  #renderPolicy(panel) {
    const type = panel.dataset.policyPanel;
    const template = this.querySelector(`[data-policy-template="${type}"]`);
    const order = this.querySelector('[data-policy-order]');
    const body = panel.querySelector('[data-policy-body]');
    const summary = panel.querySelector('[data-policy-order-summary]');
    if (!template || !order || !body || !summary) return;
    const { firstName: first, lastName: last, email } = this.#buyerValues;
    const date = new Date().toLocaleDateString('tr-TR');
    const signature = JSON.stringify([template.id, order.id, first, last, email, date]);
    // Our writes trigger the subtree observer; cache the resulting markup to
    // avoid a loop, but repair content emptied by the theme's AJAX morph.
    const rendered = this.#rendered.get(body);
    if (rendered?.signature === signature && rendered.html === panel.innerHTML) return;
    // Sanitize while detached: template scripts must never be connected first.
    const content = template.content.cloneNode(true);
    content.querySelectorAll('script, style, iframe, object, embed, form, input, button, template, link, meta, base').forEach((node) => node.remove());
    for (const element of content.querySelectorAll('*')) {
      for (const attribute of [...element.attributes]) {
        if (/^on/i.test(attribute.name) || ['srcdoc', 'id', 'name'].includes(attribute.name)
          || (/^(href|src|action|xlink:href)$/i.test(attribute.name) && /^\s*(javascript|data):/i.test(attribute.value))) {
          element.removeAttribute(attribute.name);
        }
      }
    }
    body.replaceChildren(content);
    summary.replaceChildren(order.content.cloneNode(true));
    const fullName = [first, last].filter(Boolean).join(' ') || 'Henüz girilmedi';
    const values = {
      ALICI_AD: first || 'Henüz girilmedi',
      ALICI_SOYAD: last || 'Henüz girilmedi',
      ALICI_AD_SOYAD: fullName,
      ALICI_EMAIL: email || 'Henüz girilmedi',
      SIPARIS_TOPLAMI: summary.querySelector('[data-policy-cart-total]')?.textContent.trim() ?? '',
      KARGO_TUTARI: 'Ödeme sayfasında hesaplanır',
      SOZLESME_TARIHI: date,
    };
    for (const field of panel.querySelectorAll('[data-policy-field]')) {
      field.textContent = values[field.dataset.policyField] ?? '';
    }

    // Supports the existing Word-pasted sample table without editing the
    // original static policy page or the merchant's legal clauses.
    for (const table of body.querySelectorAll('table')) {
      if (/Ürün Açıklaması/.test(table.textContent) && /Ürün\s*1/.test(table.textContent) && /0,00\s*TL/.test(table.textContent)) {
        table.replaceWith(order.content.querySelector('[data-policy-order-table]').cloneNode(true));
      }
    }
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      // Seller labels may be separate bold runs; they are not blank buyer fields.
      if (node.parentElement.closest('[data-policy-static]')) continue;
      const text = node.textContent;
      if (text.trim() === '[SIPARIS_URUNLERI]') {
        const paragraph = node.parentElement.closest('p');
        const target = paragraph?.textContent.trim() === text.trim() ? paragraph : node;
        target.replaceWith(order.content.cloneNode(true));
        continue;
      }
      const value = personalizePolicyText(text, values);
      if (value !== text) node.textContent = value;
    }
    this.#rendered.set(body, { signature, html: panel.innerHTML });
  }

  #showError(message) {
    const error = this.querySelector('[data-consent-error]');
    if (!error) return;
    if (error.textContent !== message) error.textContent = message;
    error.hidden = !message;
  }

  #updateStatus() {
    const status = this.querySelector('[data-consent-status]');
    if (!status) return;
    let message = '';
    if (this.#submitting) message = 'Bilgileriniz kaydediliyor…';
    else if (!this.#identityReady) message = 'Giriş yapın veya üye olmadan devam etmeyi seçin.';
    else if (this.#buyers.length !== 3 || this.#buyers.some((input) => !input.value.trim() || !input.validity.valid)) {
      message = 'Ad, soyad ve geçerli bir e-posta adresi girin.';
    } else if (this.closest('cart-items-component')?.classList.contains('cart-items-disabled')) {
      message = 'Sepetiniz güncelleniyor. Lütfen bekleyin.';
    } else if (this.querySelector('[data-policy-ready]')?.dataset.policyReady !== 'true') {
      message = 'Sözleşme metni yüklenemedi. Lütfen mağazayla iletişime geçin.';
    } else if (this.#checkboxes.length !== 2 || this.#checkboxes.some((input) => !input.checked)) {
      message = 'Ödemeye devam etmek için yukarıdaki iki sipariş onayı kutusunu işaretleyin.';
    }
    if (status.textContent !== message) status.textContent = message;
    if (status.hidden !== !message) status.hidden = !message;
  }

  async #checkout() {
    const revision = this.#revision;
    const buyer = this.#buyerValues;
    this.#submitting = true;
    this.#showError('');
    const time = this.querySelector('[data-consent-time]');
    if (time instanceof HTMLInputElement) time.value = new Date().toISOString();
    this.#sync();
    const attributes = {};
    for (const input of this.querySelectorAll('input[name^="attributes["]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const key = input.name.slice('attributes['.length, -1);
      attributes[key] = input.value;
    }

    try {
      const note = this.#form?.querySelector('textarea[name="note"]');
      const response = await fetch(Theme.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes, ...(note ? { note: note.value } : {}) }),
      });
      if (!response.ok) throw new Error('Cart acknowledgements could not be saved');
      const cart = await response.json();
      if (!Object.entries(attributes).every(([key, value]) => cart.attributes?.[key] === value)) {
        throw new Error('Cart acknowledgements were not confirmed');
      }
      const snapshot = JSON.parse(attributes['Sözleşme ürün bilgileri']);
      if (!matchesPolicyCart(cart, snapshot)) throw new Error('The cart changed during checkout preparation');
      // Catch autofill during the request even when no browser event fired.
      if (!this.isConnected) return;
      this.#sync();
      if (revision !== this.#revision || !this.#accepted) return;
      location.assign(buildCartCheckoutUrl(this.dataset.checkoutUrl, buyer));
    } catch (error) {
      if (time instanceof HTMLInputElement) time.value = '';
      this.#showError('Bilgileriniz kaydedilemedi veya sepetiniz değişti. Sepeti yenileyip tekrar deneyin.');
      // Do not log buyer data, cart snapshots or URLs containing contact data.
      console.warn('[cart-consent] Checkout preparation failed.');
    } finally {
      this.#submitting = false;
      if (this.isConnected) this.#sync();
    }
  }
}

if (!customElements.get('cart-consent')) {
  customElements.define('cart-consent', CartConsent);
}
